// Document Rewriter Agent using LangGraph
import { ChatOpenAI } from '@langchain/openai';
import { createReactAgent } from '@langchain/langgraph/prebuilt';
import { tool } from '@langchain/core/tools';
import { z } from 'zod';
import { DOCUMENT_REWRITER_PROMPT } from './documentRewriterPrompt.js';

// Tool to fetch available documents
const fetchDocuments = tool(
  async () => {
    console.log('🔧 TOOL: fetch_documents - Starting to fetch available documents...');
    const startTime = Date.now();

    const response = await fetch('/api/documents/list');
    if (!response.ok) {
      console.log('❌ TOOL: fetch_documents - Failed with status:', response.status);
      throw new Error('Failed to fetch available documents');
    }
    const data = await response.json();
    if (!data.success || !data.documents) {
      console.log('❌ TOOL: fetch_documents - No documents available in response');
      throw new Error('No documents available');
    }

    const endTime = Date.now();
    console.log(`✅ TOOL: fetch_documents - Completed in ${endTime - startTime}ms, found ${data.documents.length} documents`);

    // Fix for LangChain bug: Return stringified result to avoid 'messages[X].content[0].type' error
    return JSON.stringify(data.documents);
  },
  {
    name: "fetch_documents",
    description: "Fetches a list of available documents from the backend.",
    schema: z.object({}).required(),
    strict: true,
  }
);

// Tool to select a document (without extracting text)
const selectDocument = tool(
  async ({ documentName }) => {
    console.log(`🔧 TOOL: select_document - Selecting document: ${documentName}`);
    const startTime = Date.now();

    // Verify the document exists
    const response = await fetch('/api/documents/list');
    if (!response.ok) {
      console.log('❌ TOOL: select_document - Failed to fetch documents for verification');
      throw new Error('Failed to fetch available documents for verification');
    }
    const data = await response.json();
    if (!data.success || !data.documents) {
      console.log('❌ TOOL: select_document - No documents available');
      throw new Error('No documents available');
    }

    // Find the document with matching filename
    const document = data.documents.find(doc => doc.filename === documentName);
    if (!document) {
      console.log(`❌ TOOL: select_document - Document not found: ${documentName}`);
      throw new Error(`Document not found: ${documentName}`);
    }

    const endTime = Date.now();
    console.log(`✅ TOOL: select_document - Completed in ${endTime - startTime}ms, selected: ${documentName}`);

    return JSON.stringify({
      selected: true,
      documentName: documentName,
      documentPath: document.path
    });
  },
  {
    name: "select_document",
    description: "Selects a document for use without extracting its text. Use this when user specifies which document they want to use but doesn't provide enough mapping information yet.",
    schema: z.object({
      documentName: z.string().describe("The exact filename of the document to select."),
    }).required(),
    strict: true,
  }
);

// Tool to extract document text (this will be removed from chat history)
const extractDocumentText = tool(
  async ({ documentPath }) => {
    console.log(`🔧 TOOL: extract_document_text - Starting to extract text from: ${documentPath}`);
    const startTime = Date.now();

    // First, resolve the document path if it's just a filename
    let resolvedPath = documentPath;
    if (!documentPath.includes('\\') && !documentPath.includes('/')) {
      console.log('🔧 TOOL: extract_document_text - Resolving filename to full path...');
      // It's just a filename, we need to resolve it
      const documentsResponse = await fetch('/api/documents/list');
      if (!documentsResponse.ok) {
        console.log('❌ TOOL: extract_document_text - Failed to fetch documents for path resolution');
        throw new Error('Failed to fetch available documents for path resolution');
      }
      const documentsData = await documentsResponse.json();
      if (!documentsData.success || !documentsData.documents) {
        console.log('❌ TOOL: extract_document_text - No documents available for path resolution');
        throw new Error('No documents available for path resolution');
      }

      // Find the document with matching filename
      const document = documentsData.documents.find(doc => doc.filename === documentPath);
      if (document) {
        resolvedPath = document.path;
        console.log(`🔧 TOOL: extract_document_text - Resolved "${documentPath}" to "${resolvedPath}"`);
      } else {
        console.log(`❌ TOOL: extract_document_text - Document not found: ${documentPath}`);
        throw new Error(`Document not found: ${documentPath}`);
      }
    }

    console.log('🔧 TOOL: extract_document_text - Calling backend extraction API...');
    const response = await fetch('/api/documents/extract', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_path: resolvedPath })
    });

    if (!response.ok) {
      console.log(`❌ TOOL: extract_document_text - Backend extraction failed with status: ${response.status}`);
      throw new Error(`Document text extraction failed: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      console.log(`❌ TOOL: extract_document_text - Backend extraction error: ${data.error}`);
      throw new Error(`Document text extraction failed: ${data.error}`);
    }

    const endTime = Date.now();
    console.log(`✅ TOOL: extract_document_text - Completed in ${endTime - startTime}ms, extracted ${data.text.length} characters`);
    return data.text;
  },
  {
    name: "extract_document_text",
    description: "Extracts text from a specified document. You can pass either a filename or full path. If you pass a filename, the tool will automatically resolve it to the full path. This tool call will be removed from chat history to avoid cluttering it with long document content.",
    schema: z.object({
      documentPath: z.string().describe("Document filename or full path to the document."),
    }).required(),
    strict: true,
  }
);



// Create the LangGraph agent with structured output
export const createDocumentRewriterAgent = () => {
  console.log('🔑 LangGraph using backend proxy for secure API access');

  // Configure ChatOpenAI to use our backend proxy
  // We need to provide a dummy API key since LangChain requires it, but it will be ignored by our proxy
  const model = new ChatOpenAI({
    model: "gpt-4.1", // Use a real OpenAI model
    temperature: 0,
    apiKey: "dummy-key-will-be-ignored", // Dummy key required by LangChain
    configuration: {
      // baseURL: 'http://localhost:6663', // Point to our backend server
      baseURL: 'https://103lawbot.com/api', // Point to our backend server
      // LangChain will automatically append /v1/chat/completions
    }
  });

  // Define the structured output schema using Zod
  const DocumentRewriterSchema = z.object({
    intent: z.enum(["continue", "exit", "execute"]).describe("Whether to continue in document rewriter mode, exit, or execute the document rewriting"),
    selected_document: z.string().nullable().describe("The selected document filename (not full path). Use the filename from the fetch_documents tool response."),
    entity_mapping: z.record(z.string(), z.string()).nullable().describe("Mapping of original text to replacement text"),
    message: z.string().describe("Response message to show to the user")
  });

  // Use createReactAgent with system prompt and responseFormat following documentation pattern
  const agent = createReactAgent({
    llm: model,
    tools: [fetchDocuments, selectDocument, extractDocumentText], // Added selectDocument tool
    prompt: DOCUMENT_REWRITER_PROMPT,
    responseFormat: DocumentRewriterSchema
  });

  return agent;
};

// Note: Structured output schema is now defined inline in createDocumentRewriterAgent
// using Zod schema for better type safety and validation

// Global agent instance to avoid recreating it every time
let globalAgent = null;
let agentState = {
  selected_document: null,
  entity_mapping: {}
};

// Main function to handle document rewriter requests
export const handleDocumentRewriterWithLangGraph = async (query, conversationHistory) => {
  try {
    console.log('🤖 LangGraph Document Rewriter Agent processing...');

    // Create agent only once and reuse it
    if (!globalAgent) {
      console.log('🤖 Creating new LangGraph agent instance...');
      globalAgent = createDocumentRewriterAgent();
    }

    // Build conversation history, excluding document text messages
    // console.log('🤖 📚 ORIGINAL CONVERSATION HISTORY:');
    // console.log('🤖 Full conversation history objects:', JSON.stringify(conversationHistory, null, 2));

    const filteredHistory = conversationHistory
      .filter(msg => {
        // Skip messages that contain large document text (likely from extract_document_text tool)
        if (msg.content && msg.content.length > 10000) {
          console.log('🤖 Skipping large document text message from history');
          return false;
        }
        return true;
      })
      .map(msg => ({
        role: msg.role,
        content: msg.content
      }));
    // const filteredHistory = conversationHistory
    //   .filter(msg => {
    //     // Skip messages that contain large document text (likely from extract_document_text tool)
    //     if (msg.content && msg.content.length > 10000) {
    //       console.log('🤖 Skipping large document text message from history');
    //       return false;
    //     }
    //     // Skip system messages to prevent duplication
    //     if (msg.role === 'system') {
    //       console.log('🤖 Skipping system message from history to prevent duplication');
    //       return false;
    //     }
    //     return true;
    //   })
    //   .map(msg => ({
    //     role: msg.role,
    //     content: msg.content
    //   }));

    // console.log(`🤖 📝 FILTERED CONVERSATION HISTORY (${filteredHistory.length} messages):`);
    // console.log('🤖 Filtered history objects:',filteredHistory);

    // Add current state context to help the agent make better decisions
    let enhancedQuery = query;
    let stateContext = "";

    if (agentState.selected_document) {
      stateContext += `\n\nCURRENT SELECTED DOCUMENT: ${agentState.selected_document}`;
    }

    if (agentState.entity_mapping && Object.keys(agentState.entity_mapping).length > 0) {
      stateContext += `\n\nCURRENT ENTITY MAPPING:\n${JSON.stringify(agentState.entity_mapping, null, 2)}`;
    }

    if (stateContext) {
      enhancedQuery = `${query}${stateContext}`;
    }

    // Build messages array with conversation history and current query
    const messages = [
      {
        role: "system",
        content: DOCUMENT_REWRITER_PROMPT
      },
      ...filteredHistory,
      {
        role: "user",
        content: enhancedQuery
      }
    ];

    // Log the final messages array sent to the agent
    console.log('🤖 🎯 FINAL MESSAGES SENT TO LANGGRAPH AGENT:');
    console.log('🤖 Final messages array:', messages);

    // Invoke the createReactAgent with conversation history
    console.log('🤖 Invoking LangGraph createReactAgent with conversation history');
    console.log('🤖 User query:', query);
    const agentStartTime = Date.now();

    const result = await globalAgent.invoke({
      messages: messages
    });

    const agentEndTime = Date.now();
    console.log(`🤖 LangGraph agent completed in ${agentEndTime - agentStartTime}ms`);

    // createReactAgent returns structuredResponse in the result
    console.log('🤖 LangGraph Agent Response:', result);

    if (!result || !result.structuredResponse) {
      throw new Error(`LangGraph Agent returned invalid response: ${JSON.stringify(result)}`);
    }

    const structuredResponse = result.structuredResponse;
    console.log('🤖 LangGraph Agent Structured Response:', structuredResponse);

    // Update global state with new values
    if (structuredResponse.selected_document) {
      agentState.selected_document = structuredResponse.selected_document;
    }
    if (structuredResponse.entity_mapping) {
      agentState.entity_mapping = structuredResponse.entity_mapping;
    }

    // For execute intent, use the values from the structured response directly
    if (structuredResponse.intent === "execute") {
      console.log('🤖 🚀 EXECUTING DOCUMENT REWRITING...');
      try {
        const executionResult = await executeRewritingFunction(
          structuredResponse.selected_document || agentState.selected_document,
          structuredResponse.entity_mapping || agentState.entity_mapping
        );
        return {
          success: true,
          intent: "continue",
          selected_document: structuredResponse.selected_document || agentState.selected_document,
          entity_mapping: structuredResponse.entity_mapping || agentState.entity_mapping,
          message: `✅ Document rewriting completed successfully!\n\n${executionResult.message}`,
          downloadUrl: executionResult.downloadUrl,
          filename: executionResult.filename,
          documentRewriterData: {
            selectedDocument: structuredResponse.selected_document || agentState.selected_document,
            entityMapping: structuredResponse.entity_mapping || agentState.entity_mapping,
            downloadUrl: executionResult.downloadUrl,
            filename: executionResult.filename
          }
        };
      } catch (executionError) {
        console.error('❌ Document execution failed:', executionError);
        return {
          success: false,
          intent: "continue",
          selected_document: structuredResponse.selected_document || agentState.selected_document,
          entity_mapping: structuredResponse.entity_mapping || agentState.entity_mapping,
          message: `❌ Document execution failed: ${executionError.message}\n\nPlease try again or modify the mapping.`,
          documentRewriterData: {
            selectedDocument: structuredResponse.selected_document || agentState.selected_document,
            entityMapping: structuredResponse.entity_mapping || agentState.entity_mapping
          }
        };
      }
    }

    return {
      success: true,
      intent: structuredResponse.intent || "continue",
      selected_document: structuredResponse.selected_document || agentState.selected_document,
      entity_mapping: structuredResponse.entity_mapping || agentState.entity_mapping,
      message: structuredResponse.message || "Document rewriter agent completed successfully."
    };

  } catch (error) {
    console.error('❌ LangGraph Document Rewriter Agent error:', error);
    return {
      success: false,
      intent: "continue",
      selected_document: agentState.selected_document,
      entity_mapping: agentState.entity_mapping,
      message: `❌ Document Rewriter Agent encountered an error: ${error.message}\n\nPlease try again or say "exit" to leave document rewriter mode.`
    };
  }
};

// Function to execute document rewriting directly (not as a tool)
const executeRewritingFunction = async (documentPath, entityMapping) => {
  console.log(documentPath, entityMapping)
  console.log(`🔧 FUNCTION: executeRewritingFunction - Starting document rewriting for: ${documentPath}`);
  console.log(`🔧 FUNCTION: executeRewritingFunction - Entity mapping:`, entityMapping);

  // Safety check for empty/null document path
  if (!documentPath || documentPath.trim() === '' || documentPath === '.') {
    console.log('❌ FUNCTION: executeRewritingFunction - No document selected');
    throw new Error('No document selected for rewriting. Please select a document first.');
  }

  const startTime = Date.now();

  // First, resolve the document path if it's just a filename
  let resolvedPath = documentPath;
  if (!documentPath.includes('\\') && !documentPath.includes('/')) {
    console.log('🔧 FUNCTION: executeRewritingFunction - Resolving filename to full path...');
    // It's just a filename, we need to resolve it
    const documentsResponse = await fetch('/api/documents/list');
    if (!documentsResponse.ok) {
      console.log('❌ FUNCTION: executeRewritingFunction - Failed to fetch documents for path resolution');
      throw new Error('Failed to fetch available documents for path resolution');
    }
    const documentsData = await documentsResponse.json();
    if (!documentsData.success || !documentsData.documents) {
      console.log('❌ FUNCTION: executeRewritingFunction - No documents available for path resolution');
      throw new Error('No documents available for path resolution');
    }

    // Find the document with matching filename
    const document = documentsData.documents.find(doc => doc.filename === documentPath);
    if (document) {
      resolvedPath = document.path;
      console.log(`🔧 FUNCTION: executeRewritingFunction - Resolved "${documentPath}" to "${resolvedPath}"`);
    } else {
      console.log(`❌ FUNCTION: executeRewritingFunction - Document not found: ${documentPath}`);
      throw new Error(`Document not found: ${documentPath}`);
    }
  }

  console.log('🔧 FUNCTION: executeRewritingFunction - Calling backend execution API...');
  const requestBody = {
    document_path: resolvedPath,
    entity_mapping: entityMapping,
    openai_api_key: "dummy_key" // Backend expects this field even though it doesn't use it
  };
  console.log('🔧 FUNCTION: executeRewritingFunction - Request body:', JSON.stringify(requestBody, null, 2));

  const response = await fetch('/api/documents/execute', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestBody)
  });

  if (!response.ok) {
    console.log(`❌ FUNCTION: executeRewritingFunction - Backend execution failed with status: ${response.status}`);

    // Try to get the error details from the response
    try {
      const errorData = await response.text();
      console.log(`❌ FUNCTION: executeRewritingFunction - Error response body:`, errorData);
      throw new Error(`Document execution failed: ${response.status} - ${errorData}`);
    } catch (parseError) {
      console.log(`❌ FUNCTION: executeRewritingFunction - Could not parse error response:`, parseError);
      throw new Error(`Document execution failed: ${response.status}`);
    }
  }

  const result = await response.json();
  if (!result.success) {
    console.log(`❌ FUNCTION: executeRewritingFunction - Backend execution error: ${result.error}`);
    throw new Error(result.error || 'Document execution failed');
  }

  const endTime = Date.now();
  console.log(`✅ FUNCTION: executeRewritingFunction - Completed in ${endTime - startTime}ms, generated: ${result.filename}`);

  // Create detailed success message with replacement information
  let message = `Document "${result.filename}" has been generated successfully.`;
  
  if (result.replacement_summary) {
    message += `\n\n📊 Replacement Summary: ${result.replacement_summary}`;
  }
  
  if (result.has_failures && result.failed_replacements && result.failed_replacements.length > 0) {
    message += `\n\n⚠️ Warning: ${result.failed_replacements.length} placeholders were not replaced:`;
    result.failed_replacements.forEach(placeholder => {
      message += `\n   • ${placeholder}`;
    });
    message += `\n\nThese placeholders may still appear in the document due to complex formatting.`;
  }
  
  return {
    downloadUrl: result.download_url,
    filename: result.filename,
    message: message,
    replacementDetails: {
      successfulReplacements: result.successful_replacements || 0,
      failedReplacements: result.failed_replacements || [],
      totalRequested: result.total_requested || 0,
      successRate: result.success_rate || 0,
      hasFailures: result.has_failures || false
    }
  };
};

// Function to reset agent state (call when exiting document rewriter mode)
export const resetDocumentRewriterAgent = () => {
  console.log('🤖 Resetting LangGraph agent state...');
  globalAgent = null;
  agentState = {
    selected_document: null,
    entity_mapping: {}
  };
};